import React, { useState, useEffect } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  Box,
  TextField,
  Grid,
  CircularProgress,
  Button,
  IconButton,
  Alert,
  MenuItem,
  Card,
  CardContent,
  Typography,
  Chip,
  useTheme,
  useMediaQuery,
  Fade,
  Tooltip,
  InputAdornment
} from "@mui/material";
import { 
  Edit as EditIcon,
  Delete as DeleteIcon,
  Notifications as NotificationIcon,
  Search as SearchIcon,
  CalendarToday as CalendarIcon,
  Schedule as ScheduleIcon,
  Person as PersonIcon,
  Add as AddIcon,
  FilterList as FilterIcon,
  PowerOff as PowerOffIcon
} from '@mui/icons-material';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChargingStation,
  faClock,
  faCalendarAlt,
  faUser,
  faBuilding,
  faCar,
  faBolt
} from "@fortawesome/free-solid-svg-icons";
import { format, addDays, parseISO } from 'date-fns';
import { es } from 'date-fns/locale';
import { useMovilidadElectrica } from "../../context/MovilidadElectricaContext";
import { getUserByPlaceId } from "../../services/userService";
import { triggerEndCharge } from "../../services/bookingService";
import CreateBookingModal from "./CreateBookingModal";
import EditBookingModal from "./EditBookingModal";
import DeleteBookingModal from "./DeleteBookingModal";
import FacturacionModal from "./FacturacionModal";

const ReservasParqueaderosList = () => {
  const { bookings, refreshBookings, loading, error } = useMovilidadElectrica();
  const [currentPage, setCurrentPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [filters, setFilters] = useState({
    date_from: "",
    time_from: "",
    date_to: "",
    time_to: "",
    user_id: "",
  });
  const [users, setUsers] = useState([]);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isFacturacionModalOpen, setIsFacturacionModalOpen] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isTablet = useMediaQuery(theme.breakpoints.down('lg'));

  useEffect(() => {
    const fetchUsers = async () => {
      const placeId = sessionStorage.getItem("selectedPlaceId");
      if (placeId) {
        try {
          const response = await getUserByPlaceId(placeId);
          setUsers(response.data);
        } catch (error) {
          console.error("Error fetching users:", error);
        }
      }
    };

    fetchUsers();
  }, []);

  useEffect(() => {
    refreshBookings({ page: 1, page_size: 10 });
  }, [refreshBookings]);

  useEffect(() => {
    const start_from = filters.date_from && filters.time_from ? `${filters.date_from}T${filters.time_from}:00` : null;
    const start_to = filters.date_to && filters.time_to ? `${filters.date_to}T${filters.time_to}:00` : null;

    const params = {
      page: currentPage + 1,
      page_size: rowsPerPage,
      start_from,
      start_to,
      user_id: filters.user_id || null,
    };
    Object.keys(params).forEach(key => params[key] == null && delete params[key]);
    
    refreshBookings(params);
  }, [currentPage, rowsPerPage, filters]);

  useEffect(() => {
  }, [bookings]);

  const handleFilterChange = (event) => {
    const { name, value } = event.target;
    setFilters(prev => ({ ...prev, [name]: value }));
    setCurrentPage(0);
  };

  const handlePageChange = (event, newPage) => {
    setCurrentPage(newPage);
  };

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setCurrentPage(0);
  };

  const handleCreateBookingClose = (shouldRefresh) => {
    setIsCreateModalOpen(false);
    if (shouldRefresh) {
      refreshCurrentPage();
    }
  };

  const handleEditClick = (booking) => {
    setSelectedBooking(booking);
    setIsFacturacionModalOpen(true);
  };

  const handleEditBookingClose = (shouldRefresh) => {
    setIsEditModalOpen(false);
    setSelectedBooking(null);
    if (shouldRefresh) {
      refreshCurrentPage();
    }
  };

  const handleDeleteClick = (booking) => {
    setSelectedBooking(booking);
    setIsDeleteModalOpen(true);
  };

  const handleDeleteBookingClose = (shouldRefresh) => {
    setIsDeleteModalOpen(false);
    setSelectedBooking(null);
    if (shouldRefresh) {
      refreshCurrentPage();
    }
  };

  const refreshCurrentPage = () => {
    const start_from = filters.date_from && filters.time_from ? `${filters.date_from}T${filters.time_from}:00` : null;
    const start_to = filters.date_to && filters.time_to ? `${filters.date_to}T${filters.time_to}:00` : null;
    const params = {
      page: currentPage + 1,
      page_size: rowsPerPage,
      start_from,
      start_to,
      user_id: filters.user_id || null,
    };
    Object.keys(params).forEach(key => params[key] == null && delete params[key]);
    refreshBookings(params);
  };

  const handleEndChargeClick = async (bookingId) => {
    try {
      await triggerEndCharge(bookingId);
      alert('La finalización de la carga ha sido activada.');
      refreshCurrentPage();
    } catch (error) {
      console.error("Error al finalizar la carga:", error);
      alert('Error al finalizar la carga.');
    }
  };
 
  // Función para formatear el período de reserva correctamente
  const formatBookingPeriod = (bookingPeriod) => {
    if (!bookingPeriod) return '';
    
    try {
      // Asumiendo que bookingPeriod viene en formato "YYYY-MM-DD HH:mm - YYYY-MM-DD HH:mm"
      const [startStr, endStr] = bookingPeriod.split(' - ');
      
      if (!startStr || !endStr) return bookingPeriod;
      
      const startDate = parseISO(startStr.replace(' ', 'T'));
      let endDate = parseISO(endStr.replace(' ', 'T'));
      const startHour = startDate.getHours();
      const endHour = endDate.getHours();
      
      // Si es turno nocturno (termina en hora menor que la de inicio)
      // y las fechas son iguales, agregar un día a la fecha de fin
      if (endHour < startHour && 
          startDate.toDateString() === endDate.toDateString()) {
        endDate = addDays(endDate, 1);
      }
      
      const formattedStart = format(startDate, 'yyyy-MM-dd HH', { locale: es });
      const formattedEnd = format(endDate, 'yyyy-MM-dd HH', { locale: es });
      
      return `${formattedStart} - ${formattedEnd}`;
    } catch (error) {
      console.error('Error formatting booking period:', error);
      return bookingPeriod;
    }
  };

  // Función para determinar si es un turno nocturno
  const isNightShift = (bookingPeriod) => {
    if (!bookingPeriod) return false;
    
    try {
      const [startStr, endStr] = bookingPeriod.split(' - ');
      if (!startStr || !endStr) return false;
      
      const startDate = parseISO(startStr.replace(' ', 'T'));
      const endDate = parseISO(endStr.replace(' ', 'T'));
      const startHour = startDate.getHours();
      const endHour = endDate.getHours();
      
      return endHour < startHour;
    } catch (error) {
      return false;
    }
  };

  return (
    <Fade in timeout={600}>
      <Box sx={{ width: '100%' }}>
        {/* Filtros modernos */}
        <Card 
          sx={{ 
            mb: 3,
            borderRadius: { xs: 2, sm: 3 },
            border: '1px solid rgba(0, 38, 66, 0.08)',
            boxShadow: '0 4px 20px rgba(0, 38, 66, 0.08)',
            background: 'linear-gradient(135deg, #FFFFFF 0%, #FAFBFC 100%)'
          }}
        >
          <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 1.5, 
              mb: 3 
            }}>
              <FilterIcon sx={{ color: '#002642', fontSize: '1.25rem' }} />
              <Typography 
                variant="h6" 
                sx={{ 
                  fontWeight: 600,
                  color: '#002642',
                  fontSize: { xs: '1rem', sm: '1.1rem' }
                }}
              >
                Filtros de Búsqueda
              </Typography>
            </Box>
            
            <Grid container spacing={{ xs: 1.5, sm: 2, md: 2.5 }} alignItems="center">
              {/* Fechas - Móvil: stack vertical, Tablet+: horizontal */}
              <Grid item xs={12} sm={6} md={3} lg={2}>
                <TextField
                  name="date_from"
                  label="Fecha Desde"
                  type="date"
                  value={filters.date_from}
                  onChange={handleFilterChange}
                  fullWidth
                  size={isMobile ? "small" : "medium"}
                  InputLabelProps={{ shrink: true }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <CalendarIcon sx={{ color: '#75787B', fontSize: '1rem' }} />
                      </InputAdornment>
                    )
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      backgroundColor: '#FFFFFF',
                      fontSize: { xs: '0.85rem', sm: '0.875rem' },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#002642'
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#002642'
                      }
                    },
                    '& .MuiInputLabel-root': {
                      fontSize: { xs: '0.85rem', sm: '0.875rem' }
                    }
                  }}
                />
              </Grid>
              
              <Grid item xs={12} sm={6} md={3} lg={2}>
                <TextField
                  select
                  name="time_from"
                  label="Hora Desde"
                  value={filters.time_from}
                  onChange={handleFilterChange}
                  fullWidth
                  size={isMobile ? "small" : "medium"}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <ScheduleIcon sx={{ color: '#75787B', fontSize: '1rem' }} />
                      </InputAdornment>
                    )
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      backgroundColor: '#FFFFFF',
                      fontSize: { xs: '0.85rem', sm: '0.875rem' },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#002642'
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#002642'
                      }
                    },
                    '& .MuiInputLabel-root': {
                      fontSize: { xs: '0.85rem', sm: '0.875rem' }
                    }
                  }}
                >
                  {Array.from({ length: 24 }, (_, i) => (
                    <MenuItem key={i} value={`${i.toString().padStart(2, '0')}:00`}>
                      {`${i.toString().padStart(2, '0')}:00`}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              
              <Grid item xs={12} sm={6} md={3} lg={2}>
                <TextField
                  name="date_to"
                  label="Fecha Hasta"
                  type="date"
                  value={filters.date_to}
                  onChange={handleFilterChange}
                  fullWidth
                  size={isMobile ? "small" : "medium"}
                  InputLabelProps={{ shrink: true }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <CalendarIcon sx={{ color: '#75787B', fontSize: '1rem' }} />
                      </InputAdornment>
                    )
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      backgroundColor: '#FFFFFF',
                      fontSize: { xs: '0.85rem', sm: '0.875rem' },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#002642'
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#002642'
                      }
                    },
                    '& .MuiInputLabel-root': {
                      fontSize: { xs: '0.85rem', sm: '0.875rem' }
                    }
                  }}
                />
              </Grid>
              
              <Grid item xs={12} sm={6} md={3} lg={2}>
                <TextField
                  select
                  name="time_to"
                  label="Hora Hasta"
                  value={filters.time_to}
                  onChange={handleFilterChange}
                  fullWidth
                  size={isMobile ? "small" : "medium"}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <ScheduleIcon sx={{ color: '#75787B', fontSize: '1rem' }} />
                      </InputAdornment>
                    )
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      backgroundColor: '#FFFFFF',
                      fontSize: { xs: '0.85rem', sm: '0.875rem' },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#002642'
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#002642'
                      }
                    },
                    '& .MuiInputLabel-root': {
                      fontSize: { xs: '0.85rem', sm: '0.875rem' }
                    }
                  }}
                >
                  {Array.from({ length: 24 }, (_, i) => (
                    <MenuItem key={i} value={`${i.toString().padStart(2, '0')}:00`}>
                      {`${i.toString().padStart(2, '0')}:00`}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              
              <Grid item xs={12} sm={8} md={8} lg={3}>
                <TextField
                  select
                  name="user_id"
                  label="Usuario"
                  value={filters.user_id}
                  onChange={handleFilterChange}
                  fullWidth
                  size={isMobile ? "small" : "medium"}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PersonIcon sx={{ color: '#75787B', fontSize: '1rem' }} />
                      </InputAdornment>
                    )
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      backgroundColor: '#FFFFFF',
                      fontSize: { xs: '0.85rem', sm: '0.875rem' },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#002642'
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#002642'
                      }
                    },
                    '& .MuiInputLabel-root': {
                      fontSize: { xs: '0.85rem', sm: '0.875rem' }
                    }
                  }}
                >
                  <MenuItem value="">
                    <em>Todos los usuarios</em>
                  </MenuItem>
                  {users.map((user) => (
                    <MenuItem key={user.user_id} value={user.user_id}>
                      {user.username}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              
              <Grid item xs={12} sm={4} md={4} lg={1}>
                <Button 
                  variant="contained" 
                  onClick={() => setIsCreateModalOpen(true)}
                  fullWidth
                  size={isMobile ? "small" : "medium"}
                  startIcon={!isMobile && <AddIcon />}
                  sx={{
                    backgroundColor: '#002642',
                    borderRadius: 2,
                    py: { xs: 1, sm: 1.25 },
                    fontWeight: 600,
                    textTransform: 'none',
                    fontSize: { xs: '0.85rem', sm: '0.875rem' },
                    boxShadow: '0 4px 12px rgba(0, 38, 66, 0.2)',
                    '&:hover': {
                      backgroundColor: '#00577A',
                      boxShadow: '0 6px 20px rgba(0, 38, 66, 0.3)',
                      transform: 'translateY(-1px)'
                    },
                    transition: 'all 0.3s ease'
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {isMobile && <AddIcon sx={{ fontSize: '1rem' }} />}
                    Nueva Reserva
                  </Box>
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Tabla moderna */}
        <Card 
          sx={{ 
            borderRadius: { xs: 2, sm: 3 },
            border: '1px solid rgba(0, 38, 66, 0.08)',
            boxShadow: '0 4px 20px rgba(0, 38, 66, 0.08)',
            overflow: 'hidden'
          }}
        >
          {loading ? (
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'center', 
              alignItems: 'center',
              py: 8 
            }}>
              <CircularProgress 
                size={40} 
                sx={{ color: '#002642' }} 
              />
            </Box>
          ) : error ? (
            <Alert 
              severity="error" 
              sx={{ 
                m: 3,
                borderRadius: 2 
              }}
            >
              Error al cargar las reservas: {error.message}
            </Alert>
          ) : !bookings.data || bookings.data.length === 0 ? (
            <Box sx={{ 
              py: 8, 
              textAlign: 'center',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 3
            }}>
              <Box
                sx={{
                  p: 3,
                  borderRadius: 4,
                  background: 'linear-gradient(135deg, rgba(0, 38, 66, 0.05) 0%, rgba(0, 87, 122, 0.05) 100%)',
                  border: '2px dashed rgba(0, 38, 66, 0.1)'
                }}
              >
                <FontAwesomeIcon 
                  icon={faChargingStation} 
                  style={{ 
                    color: '#75787B', 
                    fontSize: '3rem'
                  }}
                />
              </Box>
              <Typography 
                variant="h6" 
                sx={{ 
                  color: '#002642',
                  fontWeight: 600
                }}
              >
                No hay reservas disponibles
              </Typography>
              <Typography 
                variant="body2" 
                sx={{ 
                  color: '#75787B',
                  maxWidth: 400,
                  lineHeight: 1.5
                }}
              >
                No se encontraron reservas con los filtros aplicados. 
                Intenta ajustar los criterios de búsqueda o crear una nueva reserva.
              </Typography>
            </Box>
          ) : isMobile ? (
            /* Vista Mobile - Cards */
            <Box sx={{ p: 2 }}>
              <Grid container spacing={2}>
                {bookings.data.map((reserva, index) => (
                  <Grid item xs={12} key={reserva.booking_id}>
                    <Card
                      sx={{
                        borderRadius: 3,
                        border: '1px solid rgba(0, 38, 66, 0.08)',
                        boxShadow: '0 2px 12px rgba(0, 38, 66, 0.08)',
                        background: 'linear-gradient(135deg, #FFFFFF 0%, #FAFBFC 100%)',
                        '&:hover': {
                          boxShadow: '0 4px 20px rgba(0, 38, 66, 0.15)',
                          transform: 'translateY(-2px)'
                        },
                        transition: 'all 0.3s ease'
                      }}
                    >
                      <CardContent sx={{ p: 2 }}>
                        {/* Header del card */}
                        <Box sx={{ 
                          display: 'flex', 
                          justifyContent: 'space-between', 
                          alignItems: 'flex-start',
                          mb: 2
                        }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box
                              sx={{
                                p: 1,
                                borderRadius: 2,
                                backgroundColor: 'rgba(0, 38, 66, 0.1)'
                              }}
                            >
                              <FontAwesomeIcon 
                                icon={faChargingStation} 
                                style={{ color: '#002642', fontSize: '1rem' }}
                              />
                            </Box>
                            <Box>
                              <Typography 
                                variant="h6" 
                                sx={{ 
                                  fontWeight: 700,
                                  color: '#002642',
                                  fontSize: '1rem',
                                  lineHeight: 1.2
                                }}
                              >
                                Celda {reserva.location}
                              </Typography>
                              <Typography 
                                variant="body2" 
                                sx={{ 
                                  color: '#75787B',
                                  fontSize: '0.8rem'
                                }}
                              >
                                {reserva.booking_time} horas
                              </Typography>
                            </Box>
                          </Box>
                          
                          <Box sx={{ textAlign: 'right' }}>
                            <Chip 
                              label={`${reserva.kwh_consumed} kW`}
                              size="small"
                              sx={{
                                backgroundColor: '#DB9600',
                                color: '#FFFFFF',
                                fontWeight: 600,
                                fontSize: '0.75rem'
                              }}
                            />
                            {isNightShift(reserva.booking_period) && (
                              <Chip 
                                label="Nocturno" 
                                size="small" 
                                sx={{ 
                                  backgroundColor: '#002642',
                                  color: '#FFFFFF',
                                  fontSize: '0.7rem',
                                  ml: 0.5,
                                  mt: 0.5
                                }} 
                              />
                            )}
                          </Box>
                        </Box>

                        {/* Información principal */}
                        <Box sx={{ mb: 2 }}>
                          <Box sx={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: 1, 
                            mb: 1 
                          }}>
                            <FontAwesomeIcon 
                              icon={faCalendarAlt} 
                              style={{ color: '#75787B', fontSize: '0.8rem' }}
                            />
                            <Typography 
                              variant="body2" 
                              sx={{ 
                                color: '#002642',
                                fontWeight: 500,
                                fontSize: '0.85rem',
                                fontFamily: 'monospace'
                              }}
                            >
                              {formatBookingPeriod(reserva.booking_period)}
                            </Typography>
                          </Box>
                        </Box>

                        {/* Detalles en grid */}
                        <Grid container spacing={2} sx={{ mb: 2 }}>
                          <Grid item xs={6}>
                            <Box sx={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              gap: 1 
                            }}>
                              <FontAwesomeIcon 
                                icon={faUser} 
                                style={{ color: '#75787B', fontSize: '0.8rem' }}
                              />
                              <Box>
                                <Typography 
                                  variant="caption" 
                                  sx={{ 
                                    color: '#75787B',
                                    fontSize: '0.7rem',
                                    display: 'block'
                                  }}
                                >
                                  Usuario
                                </Typography>
                                <Typography 
                                  variant="body2" 
                                  sx={{ 
                                    color: '#002642',
                                    fontWeight: 500,
                                    fontSize: '0.8rem'
                                  }}
                                >
                                  {reserva.username}
                                </Typography>
                              </Box>
                            </Box>
                          </Grid>
                          
                          <Grid item xs={6}>
                            <Box sx={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              gap: 1 
                            }}>
                              <FontAwesomeIcon 
                                icon={faBuilding} 
                                style={{ color: '#75787B', fontSize: '0.8rem' }}
                              />
                              <Box>
                                <Typography 
                                  variant="caption" 
                                  sx={{ 
                                    color: '#75787B',
                                    fontSize: '0.7rem',
                                    display: 'block'
                                  }}
                                >
                                  Apartamento
                                </Typography>
                                <Typography 
                                  variant="body2" 
                                  sx={{ 
                                    color: '#75787B',
                                    fontSize: '0.8rem'
                                  }}
                                >
                                  {reserva.matricula}
                                </Typography>
                              </Box>
                            </Box>
                          </Grid>
                          
                          <Grid item xs={12}>
                            <Box sx={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              gap: 1 
                            }}>
                              <FontAwesomeIcon 
                                icon={faCar} 
                                style={{ color: '#75787B', fontSize: '0.8rem' }}
                              />
                              <Box>
                                <Typography 
                                  variant="caption" 
                                  sx={{ 
                                    color: '#75787B',
                                    fontSize: '0.7rem',
                                    display: 'block'
                                  }}
                                >
                                  Vehículo
                                </Typography>
                                <Typography 
                                  variant="body2" 
                                  sx={{ 
                                    color: '#002642',
                                    fontWeight: 500,
                                    fontSize: '0.8rem'
                                  }}
                                >
                                  {reserva.plate}
                                </Typography>
                              </Box>
                            </Box>
                          </Grid>
                        </Grid>

                        {/* Acciones */}
                        <Box sx={{ 
                          display: 'flex', 
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          pt: 1,
                          borderTop: '1px solid rgba(0, 38, 66, 0.06)'
                        }}>
                          <Typography 
                            variant="caption" 
                            sx={{ 
                              color: '#75787B',
                              fontSize: '0.7rem'
                            }}
                          >
                            Reserva #{reserva.booking_id}
                          </Typography>
                          
                          <Box sx={{ display: 'flex', gap: 0.5 }}>
                            <Tooltip title="Editar">
                              <IconButton 
                                size="small"
                                onClick={() => handleEditClick(reserva)}
                                sx={{
                                  color: '#002642',
                                  backgroundColor: 'rgba(0, 38, 66, 0.05)',
                                  '&:hover': {
                                    backgroundColor: 'rgba(0, 38, 66, 0.1)',
                                    transform: 'scale(1.05)'
                                  }
                                }}
                              >
                                <EditIcon sx={{ fontSize: '0.9rem' }} />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Eliminar">
                              <IconButton 
                                size="small"
                                onClick={() => handleDeleteClick(reserva)}
                                sx={{
                                  color: '#d32f2f',
                                  backgroundColor: 'rgba(211, 47, 47, 0.05)',
                                  '&:hover': {
                                    backgroundColor: 'rgba(211, 47, 47, 0.1)',
                                    transform: 'scale(1.05)'
                                  }
                                }}
                              >
                                <DeleteIcon sx={{ fontSize: '0.9rem' }} />
                              </IconButton>
                            </Tooltip>
                            {new Date(reserva.end_date) < new Date() && (
                              <Tooltip title={reserva.status === 'completed' ? "Carga ya finalizada" : "Finalizar Carga"}>
                                <span>
                                  <IconButton
                                    size="small"
                                    onClick={() => handleEndChargeClick(reserva.booking_id)}
                                    disabled={reserva.status === 'completed'}
                                    sx={{
                                      color: reserva.status === 'completed' ? '#d32f2f' : '#1E8449',
                                      backgroundColor: reserva.status === 'completed' ? 'rgba(211, 47, 47, 0.05)' : 'rgba(30, 132, 73, 0.05)',
                                      '&:hover': {
                                        backgroundColor: reserva.status === 'completed' ? 'rgba(211, 47, 47, 0.1)' : 'rgba(30, 132, 73, 0.1)',
                                        transform: 'scale(1.05)'
                                      },
                                      cursor: reserva.status === 'completed' ? 'not-allowed' : 'pointer'
                                    }}
                                  >
                                    <PowerOffIcon sx={{ fontSize: '0.9rem' }} />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            )}
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
              
              {/* Paginación para móvil */}
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'center',
                mt: 3,
                p: 2
              }}>
                <TablePagination
                  component="div"
                  count={bookings.total || 0}
                  page={currentPage}
                  onPageChange={handlePageChange}
                  rowsPerPage={rowsPerPage}
                  onRowsPerPageChange={handleRowsPerPageChange}
                  rowsPerPageOptions={[5, 10, 15]}
                  labelRowsPerPage="Por página:"
                  labelDisplayedRows={({ from, to, count }) =>
                    `${from}-${to} de ${count !== -1 ? count : `más de ${to}`}`
                  }
                  sx={{
                    '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                      color: '#002642',
                      fontWeight: 500,
                      fontSize: { xs: '0.75rem', sm: '0.875rem' }
                    },
                    '& .MuiTablePagination-select': {
                      color: '#002642'
                    },
                    '& .MuiIconButton-root': {
                      color: '#002642'
                    }
                  }}
                />
              </Box>
            </Box>
          ) : (
            /* Vista Desktop/Tablet - Tabla optimizada */
            <>
              <TableContainer>
              <Table size={isTablet ? "small" : "medium"}>
                <TableHead sx={{ backgroundColor: '#002642' }}>
                  <TableRow>
                    <TableCell sx={{ 
                      color: '#FFFFFF', 
                      fontWeight: 600,
                      fontSize: { sm: '0.8rem', md: '0.875rem' },
                      py: { sm: 1.5, md: 2 },
                      width: '12%'
                    }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <FontAwesomeIcon 
                          icon={faChargingStation} 
                          style={{ fontSize: '0.8rem' }} 
                        />
                        {!isTablet && 'Celda'}
                      </Box>
                    </TableCell>
                    <TableCell sx={{ 
                      color: '#FFFFFF', 
                      fontWeight: 600,
                      fontSize: { sm: '0.8rem', md: '0.875rem' },
                      py: { sm: 1.5, md: 2 },
                      width: '10%'
                    }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <FontAwesomeIcon 
                          icon={faClock} 
                          style={{ fontSize: '0.8rem' }} 
                        />
                        {!isTablet && 'Horas'}
                      </Box>
                    </TableCell>
                    <TableCell sx={{ 
                      color: '#FFFFFF', 
                      fontWeight: 600,
                      fontSize: { sm: '0.8rem', md: '0.875rem' },
                      py: { sm: 1.5, md: 2 },
                      width: '25%'
                    }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <FontAwesomeIcon 
                          icon={faCalendarAlt} 
                          style={{ fontSize: '0.8rem' }} 
                        />
                        Período
                      </Box>
                    </TableCell>
                    <TableCell sx={{ 
                      color: '#FFFFFF', 
                      fontWeight: 600,
                      fontSize: { sm: '0.8rem', md: '0.875rem' },
                      py: { sm: 1.5, md: 2 },
                      width: '15%'
                    }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <FontAwesomeIcon 
                          icon={faUser} 
                          style={{ fontSize: '0.8rem' }} 
                        />
                        Usuario
                      </Box>
                    </TableCell>
                    {!isTablet && (
                      <TableCell sx={{ 
                        color: '#FFFFFF', 
                        fontWeight: 600,
                        fontSize: '0.875rem',
                        py: 2,
                        width: '12%'
                      }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <FontAwesomeIcon 
                            icon={faBuilding} 
                            style={{ fontSize: '0.8rem' }} 
                          />
                          Apto.
                        </Box>
                      </TableCell>
                    )}
                    <TableCell sx={{ 
                      color: '#FFFFFF', 
                      fontWeight: 600,
                      fontSize: { sm: '0.8rem', md: '0.875rem' },
                      py: { sm: 1.5, md: 2 },
                      width: isTablet ? '15%' : '12%'
                    }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <FontAwesomeIcon 
                          icon={faCar} 
                          style={{ fontSize: '0.8rem' }} 
                        />
                        Vehículo
                      </Box>
                    </TableCell>
                    <TableCell sx={{ 
                      color: '#FFFFFF', 
                      fontWeight: 600,
                      fontSize: { sm: '0.8rem', md: '0.875rem' },
                      py: { sm: 1.5, md: 2 },
                      width: '10%'
                    }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <FontAwesomeIcon 
                          icon={faBolt} 
                          style={{ fontSize: '0.8rem' }} 
                        />
                        kW
                      </Box>
                    </TableCell>
                    <TableCell sx={{ 
                      color: '#FFFFFF', 
                      fontWeight: 600,
                      fontSize: { sm: '0.8rem', md: '0.875rem' },
                      py: { sm: 1.5, md: 2 },
                      textAlign: 'center',
                      width: isTablet ? '14%' : '10%'
                    }}>
                      Acciones
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {bookings.data.map((reserva, index) => (
                    <TableRow 
                      key={reserva.booking_id}
                      sx={{
                        backgroundColor: index % 2 === 0 ? '#FFFFFF' : '#FAFBFC',
                        '&:hover': {
                          backgroundColor: '#F0F8FF',
                          transform: 'scale(1.001)',
                          boxShadow: '0 2px 8px rgba(0, 38, 66, 0.1)'
                        },
                        transition: 'all 0.2s ease'
                      }}
                    >
                      <TableCell sx={{ 
                        py: { sm: 1, md: 1.5 },
                        fontSize: { sm: '0.75rem', md: '0.8rem' }
                      }}>
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            fontWeight: 600,
                            color: '#002642',
                            fontSize: { sm: '0.75rem', md: '0.8rem' }
                          }}
                        >
                          {reserva.location}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ 
                        py: { sm: 1, md: 1.5 },
                        fontSize: { sm: '0.75rem', md: '0.8rem' }
                      }}>
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            color: '#75787B',
                            fontSize: { sm: '0.75rem', md: '0.8rem' }
                          }}
                        >
                          {reserva.booking_time}h
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ 
                        py: { sm: 1, md: 1.5 },
                        fontSize: { sm: '0.75rem', md: '0.8rem' }
                      }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              color: '#002642',
                              fontWeight: 500,
                              fontFamily: 'monospace',
                              fontSize: { sm: '0.7rem', md: '0.75rem' }
                            }}
                          >
                            {formatBookingPeriod(reserva.booking_period)}
                          </Typography>
                          {isNightShift(reserva.booking_period) && (
                            <Chip 
                              label="Nocturno" 
                              size="small" 
                              sx={{ 
                                backgroundColor: '#002642',
                                color: '#FFFFFF',
                                fontSize: '0.6rem',
                                height: 18,
                                alignSelf: 'flex-start'
                              }} 
                            />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell sx={{ 
                        py: { sm: 1, md: 1.5 },
                        fontSize: { sm: '0.75rem', md: '0.8rem' }
                      }}>
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            color: '#002642',
                            fontWeight: 500,
                            fontSize: { sm: '0.75rem', md: '0.8rem' }
                          }}
                        >
                          {reserva.username}
                        </Typography>
                      </TableCell>
                      {!isTablet && (
                        <TableCell sx={{ 
                          py: 1.5,
                          fontSize: '0.8rem'
                        }}>
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              color: '#75787B',
                              fontSize: '0.8rem'
                            }}
                          >
                            {reserva.matricula}
                          </Typography>
                        </TableCell>
                      )}
                      <TableCell sx={{ 
                        py: { sm: 1, md: 1.5 },
                        fontSize: { sm: '0.75rem', md: '0.8rem' }
                      }}>
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            color: '#002642',
                            fontWeight: 500,
                            fontSize: { sm: '0.75rem', md: '0.8rem' }
                          }}
                        >
                          {reserva.plate}
                          {isTablet && (
                            <Typography 
                              variant="caption" 
                              sx={{ 
                                color: '#75787B',
                                fontSize: '0.65rem',
                                display: 'block'
                              }}
                            >
                              {reserva.matricula}
                            </Typography>
                          )}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ 
                        py: { sm: 1, md: 1.5 },
                        fontSize: { sm: '0.75rem', md: '0.8rem' }
                      }}>
                        <Chip 
                          label={`${reserva.kwh_consumed} kW`}
                          size="small"
                          sx={{
                            backgroundColor: '#DB9600',
                            color: '#FFFFFF',
                            fontWeight: 600,
                            fontSize: { sm: '0.65rem', md: '0.7rem' }
                          }}
                        />
                      </TableCell>
                      <TableCell sx={{ 
                        py: { sm: 1, md: 1.5 },
                        textAlign: 'center'
                      }}>
                        <Box sx={{ 
                          display: 'flex', 
                          gap: 0.25, 
                          justifyContent: 'center',
                          flexWrap: 'wrap'
                        }}>
                          <Tooltip title="Editar">
                            <IconButton 
                              size="small"
                              onClick={() => handleEditClick(reserva)}
                              sx={{
                                color: '#002642',
                                '&:hover': {
                                  backgroundColor: 'rgba(0, 38, 66, 0.1)',
                                  transform: 'scale(1.05)'
                                }
                              }}
                            >
                              <EditIcon sx={{ fontSize: '0.9rem' }} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Eliminar">
                            <IconButton 
                              size="small"
                              onClick={() => handleDeleteClick(reserva)}
                              sx={{
                                color: '#d32f2f',
                                '&:hover': {
                                  backgroundColor: 'rgba(211, 47, 47, 0.1)',
                                  transform: 'scale(1.05)'
                                }
                              }}
                            >
                              <DeleteIcon sx={{ fontSize: '0.9rem' }} />
                            </IconButton>
                          </Tooltip>
                          {new Date(reserva.end_date) < new Date() && (
                            <Tooltip title={reserva.status === 'completed' ? "Carga ya finalizada" : "Finalizar Carga"}>
                              <span>
                                <IconButton
                                  size="small"
                                  onClick={() => handleEndChargeClick(reserva.booking_id)}
                                  disabled={reserva.status === 'completed'}
                                  sx={{
                                    color: reserva.status === 'completed' ? '#d32f2f' : '#1E8449',
                                    '&:hover': {
                                      backgroundColor: reserva.status === 'completed' ? 'rgba(211, 47, 47, 0.1)' : 'rgba(30, 132, 73, 0.1)',
                                      transform: 'scale(1.05)'
                                    },
                                    cursor: reserva.status === 'completed' ? 'not-allowed' : 'pointer'
                                  }}
                                >
                                  <PowerOffIcon sx={{ fontSize: '0.9rem' }} />
                                </IconButton>
                              </span>
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
              
            <TablePagination
              component="div"
              count={bookings.total || 0}
              page={currentPage}
              onPageChange={handlePageChange}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleRowsPerPageChange}
              rowsPerPageOptions={[5, 10, 15]}
              labelRowsPerPage="Filas por página:"
              labelDisplayedRows={({ from, to, count }) =>
                `${from}-${to} de ${count !== -1 ? count : `más de ${to}`}`
              }
              sx={{
                backgroundColor: '#FAFBFC',
                borderTop: '1px solid rgba(0, 38, 66, 0.08)',
                '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                  color: '#002642',
                  fontWeight: 500
                },
                '& .MuiTablePagination-select': {
                  color: '#002642'
                },
                '& .MuiIconButton-root': {
                  color: '#002642'
                }
              }}
            />
            </>
          )}
        </Card>

        {/* Modales */}
        <CreateBookingModal
          isOpen={isCreateModalOpen}
          onClose={handleCreateBookingClose}
          selectedUserId={filters.user_id}
        />
        <EditBookingModal
          isOpen={isEditModalOpen}
          onClose={handleEditBookingClose}
          booking={selectedBooking}
        />
        <DeleteBookingModal
          isOpen={isDeleteModalOpen}
          onClose={handleDeleteBookingClose}
          booking={selectedBooking}
        />
        <FacturacionModal
          isOpen={isFacturacionModalOpen}
          onClose={() => setIsFacturacionModalOpen(false)}
          reserva={selectedBooking}
        />
      </Box>
    </Fade>
  );
};

export default ReservasParqueaderosList;
